import { cn } from '../lib/cn'

export type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg'

const SIZES: Record<SpinnerSize, string> = {
  xs: 'h-3 w-3 border-[1.5px]',
  sm: 'h-4 w-4 border-2',
  md: 'h-5 w-5 border-2',
  lg: 'h-8 w-8 border-[3px]',
}

export interface SpinnerProps {
  size?: SpinnerSize
  className?: string
  /** Announced to screen readers. Pass null inside a control that already says it. */
  label?: string | null
}

export function Spinner({ size = 'md', className, label = 'Loading' }: SpinnerProps) {
  return (
    <span
      role={label ? 'status' : undefined}
      aria-label={label ?? undefined}
      className={cn(
        'inline-block animate-spin rounded-full border-current border-r-transparent align-[-0.125em] opacity-70',
        SIZES[size],
        className,
      )}
    />
  )
}

/** A thin progress bar for work whose length is unknown, such as a brand switch. */
export function IndeterminateBar({ className }: { className?: string }) {
  return (
    <div
      className={cn('h-0.5 w-full overflow-hidden bg-accent/15', className)}
      role="progressbar"
      aria-label="Working"
    >
      <div className="h-full w-1/4 animate-indeterminate-bar rounded-full bg-accent" />
    </div>
  )
}

/** Grey block that stands in for content while it loads. */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('skeleton h-4 w-full', className)} aria-hidden="true" />
}

/** Several skeleton lines, for a paragraph or a list. */
export function SkeletonLines({ count = 3, className }: { count?: number; className?: string }) {
  return (
    <div className={cn('space-y-2', className)} aria-hidden="true">
      {Array.from({ length: count }, (_, index) => (
        <Skeleton key={index} className={index === count - 1 ? 'w-2/3' : 'w-full'} />
      ))}
    </div>
  )
}

/**
 * Rows of skeleton cells while a table loads. Sized like the real table so the
 * page does not jump when the data lands.
 */
export function SkeletonTable({ rows = 8, cols = 6 }: { rows?: number; cols?: number }) {
  return (
    <div className="p-3" role="status" aria-live="polite" aria-label="Loading rows">
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className="flex items-center gap-3 border-b border-line py-2.5 last:border-0">
          {Array.from({ length: cols }, (_, col) => (
            <Skeleton
              key={col}
              className={cn('h-3', col === 0 ? 'w-48' : col === cols - 1 ? 'w-16' : 'w-24')}
            />
          ))}
        </div>
      ))}
    </div>
  )
}
